import React, { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LogOut, Clock, CheckCircle, XCircle } from 'lucide-react';

const AccountPendingPage = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Poll for status updates every 30 seconds
    const interval = setInterval(() => {
      refreshUser();
    }, 30000);

    return () => clearInterval(interval);
  }, [refreshUser]);

  useEffect(() => {
    // If account is approved, redirect to home or edit profile
    if (user?.accountApproved) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getStatusInfo = () => {
    const status = user?.accountStatus || 'pending';
    
    switch(status) {
      case 'approved':
        return {
          icon: <CheckCircle className="w-16 h-16 text-green-500" />,
          title: 'Account Approved!',
          message: 'Your account has been approved.',
          detail: 'You can now start browsing profiles. Redirecting...',
          color: 'green'
        };
      case 'rejected':
        return {
          icon: <XCircle className="w-16 h-16 text-red-500" />,
          title: 'Account Not Approved',
          message: 'Unfortunately, your account registration was not approved.',
          detail: user?.accountRejectionReason || 'Please contact support for more information.',
          color: 'red'
        };
      default:
        return {
          icon: <Clock className="w-16 h-16 text-yellow-500" />,
          title: 'Account Under Review',
          message: 'Thank you for registering! Your account is being reviewed.',
          detail: 'Our team will review your registration within 24-48 hours. You will receive an email once your account is approved.',
          color: 'yellow'
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 flex items-center justify-center p-4">
      <div className="max-w-xl w-full">
        {/* Main Status Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            {statusInfo.icon}
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {statusInfo.title}
          </h1>

          {/* Message */}
          <p className="text-lg text-gray-700 mb-4">
            {statusInfo.message}
          </p>

          {/* Detail */}
          <p className="text-base text-gray-600 mb-8">
            {statusInfo.detail}
          </p>

          {/* Status Badge */}
          <div className="inline-flex items-center space-x-2 bg-gray-100 rounded-full px-6 py-3 mb-8">
            <div className={`w-3 h-3 rounded-full ${
              statusInfo.color === 'yellow' ? 'bg-yellow-500 animate-pulse' :
              statusInfo.color === 'green' ? 'bg-green-500' :
              'bg-red-500'
            }`}></div>
            <span className="text-sm font-semibold text-gray-700">
              Status: {(user?.accountStatus || 'pending').toUpperCase()}
            </span>
          </div>

          {/* Timeline for pending */}
          {user?.accountStatus !== 'approved' && user?.accountStatus !== 'rejected' && (
            <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left">
              <h3 className="font-semibold text-gray-900 mb-4">What happens next?</h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Registration Complete</p>
                    <p className="text-sm text-gray-600">Your basic information has been submitted</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-yellow-500 flex items-center justify-center flex-shrink-0 mt-0.5 animate-pulse">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Under Review</p>
                    <p className="text-sm text-gray-600">Our team is reviewing your registration</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs text-gray-600">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-500">Account Approved</p>
                    <p className="text-sm text-gray-500">Start browsing profiles on qbool</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* User Info */}
          {user && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-gray-600"><strong>Name:</strong> {user.firstName} {user.lastName}</p>
              <p className="text-sm text-gray-600"><strong>Email:</strong> {user.email}</p>
            </div>
          )}

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="flex items-center justify-center space-x-2 mx-auto text-gray-600 hover:text-gray-900 transition-colors duration-200"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>

        {/* Footer Note */}
        <div className="mt-6 text-center text-sm text-gray-600">
          <p>Questions? Email us at <a href="mailto:quboolme@gmail.com" className="text-navy-500 hover:underline">quboolme@gmail.com</a></p>
        </div>
      </div>
    </div>
  );
};

export default AccountPendingPage;
